import { memo, useEffect, useRef, useCallback, useState } from 'react'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import { isLockedByOtherUser, isLockedByCurrentUser } from 'services/Registrations'
import { getAccessToken } from 'services/Authentication'
import { CS } from 'back-end-api'
import { useTranslation } from 'services/Translation'
import { ReadOnlyProvider } from 'components/patient/ReadOnlyContext'
import { LockSyncProvider } from 'components/patient/LockSyncContext'
import { usePatientRouteParams } from 'components/patient/Parameters'
import Header from './Header'
import Tabs from './Tabs'

const cedeRegistrationOnUnload = (registrationId) => {
  const token = getAccessToken()
  const baseUrl = process.env.REACT_APP_CLINIC_SERVICE_URL || ''
  const url = `${baseUrl}/CedeRegistration`
  const body = JSON.stringify({ id: registrationId })

  // Try fetch with keepalive first (survives page unload, supports headers)
  try {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body,
      keepalive: true,
    })
  } catch {
    // Fallback: sendBeacon (no auth header, but at least the request fires)
    navigator.sendBeacon?.(url, new Blob([body], { type: 'application/json' }))
  }
}

const texts = getText => key =>
  ({
    readOnlyBanner: getText('You are viewing this summary in read-only mode. Actions are disabled.', 'Patient_Details_ReadOnly'),
    emergencyUnlock: getText('Emergency Unlock', 'Patient_Details_ReadOnly'),
    emergencyTitle: getText('Emergency Unlock', 'Patient_Details_ReadOnly_Emergency'),
    emergencyMessage: getText(
      'This action will force-unlock the summary, removing the other doctor\'s lock. Only use this in urgent scenarios where the locking doctor is unavailable and immediate access is required.',
      'Patient_Details_ReadOnly_Emergency'
    ),
    emergencyConfirm: getText('Yes, unlock now', 'Patient_Details_ReadOnly_Emergency'),
    emergencyCancel: getText('Cancel', 'Patient_Details_ReadOnly_Emergency'),
  }[key])

const PatientContent = () => {
  const { t } = useTranslation(texts)
  const token = getAccessToken()
  const { id } = useParams()
  const history = useHistory()
  const location = useLocation()
  const patientId = parseInt(id, 10)

  // Read-only mode from query param (set when viewing a locked summary)
  const searchParams = new URLSearchParams(location.search)
  const isReadOnly = searchParams.get('readOnly') === 'true'

  // Emergency unlock state
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false)
  const [unlocking, setUnlocking] = useState(false)

  const handleEmergencyUnlock = useCallback(async () => {
    setUnlocking(true)
    try {
      await CS.cedeRegistration({ id: patientId })
      // Remove readOnly param and reload the page to re-enter in edit mode
      const params = new URLSearchParams(location.search)
      params.delete('readOnly')
      const newSearch = params.toString()
      history.replace(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`)
      window.location.reload()
    } catch {
      setUnlocking(false)
      setShowUnlockConfirm(false)
    }
  }, [patientId, history, location])

  // Single source of truth: CS.useRegistration via usePatientRouteParams
  const { registration, summary, appointment } = usePatientRouteParams()

  const patient = registration
    ? {
        id: registration.id,
        dob: registration.date_of_birth,
        firstname: registration.first_name,
        lastname: registration.last_name,
        gender: registration.gender,
        email: registration.email,
        phone: registration.mobile_phone,
        ssn: registration.social_security_number,
      }
    : null

  // Auto-lock: claim registration on mount (if not read-only and not locked by someone else)
  // Auto-unlock: cede registration 30s after unmount, or immediately on tab/browser close
  const autoLocked = useRef(false)
  // Keep latest registration data accessible in closures (cleanup, beforeunload)
  const registrationRef = useRef(registration)
  registrationRef.current = registration
  // Patient-scoped unlock timers: Map<registrationId, timerId>
  // Each patient gets its own independent timer so navigating between patients
  // does not cancel or interfere with another patient's delayed unlock.
  const unlockTimersRef = useRef(new Map())
  const patientIdRef = useRef(patientId)
  patientIdRef.current = patientId

  const handleBeforeUnload = useCallback(() => {
    // Use server state (registration data) as primary check — if lock was already
    // released manually, this is a no-op. autoLocked ref kept as secondary signal.
    const reg = registrationRef.current
    if (reg && isLockedByCurrentUser(reg)) {
      cedeRegistrationOnUnload(patientIdRef.current)
      autoLocked.current = false
    }
  }, [])

  // Callback for LockAction to sync autoLocked ref after manual lock/unlock
  const handleLockSync = useCallback((locked) => {
    autoLocked.current = locked
  }, [])

  // Clean up ALL timers when the component is truly unmounted (final cleanup)
  useEffect(() => {
    return () => {
      unlockTimersRef.current.forEach((timerId) => clearTimeout(timerId))
      unlockTimersRef.current.clear()
    }
  }, [])

  useEffect(() => {
    if (!registration || isReadOnly) return () => {}

    const currentRegId = patientId

    // Cancel only THIS patient's pending unlock timer (re-mount for same patient)
    if (unlockTimersRef.current.has(currentRegId)) {
      clearTimeout(unlockTimersRef.current.get(currentRegId))
      unlockTimersRef.current.delete(currentRegId)
    }

    // Only auto-lock if not already locked by another user
    if (!isLockedByOtherUser(registration) && !autoLocked.current) {
      CS.claimRegistration({ id: currentRegId }).then(() => {
        autoLocked.current = true
        window.addEventListener('beforeunload', handleBeforeUnload)
      }).catch(() => {})
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      // Use server state as primary check for cleanup — if lock was already
      // released manually (doctor_account_id == 0), cleanup is a no-op.
      // autoLocked ref kept as secondary signal for LockSync context.
      const reg = registrationRef.current
      const shouldCede = reg ? isLockedByCurrentUser(reg) : autoLocked.current
      if (shouldCede) {
        const regId = currentRegId
        autoLocked.current = false
        const timerId = setTimeout(() => {
          CS.cedeRegistration({ id: regId }).catch((err) => {
            console.warn(
              `[LockSync] Failed to cede registration ${regId} during cleanup. ` +
              `Server-side TTL will release the lock automatically.`,
              err
            )
          })
          unlockTimersRef.current.delete(regId)
        }, 30000)
        unlockTimersRef.current.set(regId, timerId)
      }
    }
  }, [registration?.id, isReadOnly]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ReadOnlyProvider value={isReadOnly}>
      <LockSyncProvider value={handleLockSync}>
      <div style={{ marginTop: '6.375rem', display: 'flex', flexDirection: 'column', height: 'calc(100% - 6.375rem)' }}>
        {isReadOnly && (
          <>
            <div style={{
              backgroundColor: '#FEF3C7',
              borderBottom: '1px solid #F59E0B',
              padding: '0.5rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              fontFamily: 'coresanscr55, sans-serif',
              fontSize: '0.875rem',
              color: '#92400E',
            }}>
              <span>🔒 {t('readOnlyBanner')}</span>
              <button
                type="button"
                onClick={() => setShowUnlockConfirm(true)}
                style={{
                  background: '#DC2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.25rem 0.75rem',
                  fontSize: '0.8rem',
                  fontFamily: 'coresanscr65, sans-serif',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                🚨 {t('emergencyUnlock')}
              </button>
            </div>

            {showUnlockConfirm && (
              <div
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Escape' && !unlocking) setShowUnlockConfirm(false) }}
                style={{
                  position: 'fixed',
                  inset: 0,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 9999,
                }}
                onClick={e => { if (e.target === e.currentTarget && !unlocking) setShowUnlockConfirm(false) }}
              >
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-label={t('emergencyTitle')}
                  style={{
                    background: '#fff',
                    borderRadius: '0.75rem',
                    padding: '2rem',
                    maxWidth: '28rem',
                    width: '90%',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    border: '2px solid #DC2626',
                  }}
                >
                  <div style={{
                    fontSize: '1.25rem',
                    fontFamily: 'coresanscr65, sans-serif',
                    color: '#DC2626',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}>
                    ⚠️ {t('emergencyTitle')}
                  </div>
                  <p style={{
                    fontSize: '0.9rem',
                    color: '#1C165B',
                    lineHeight: 1.6,
                    marginBottom: '1.5rem',
                    fontFamily: 'coresanscr45, sans-serif',
                  }}>
                    {t('emergencyMessage')}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                    <button
                      type="button"
                      onClick={() => setShowUnlockConfirm(false)}
                      disabled={unlocking}
                      style={{
                        background: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        padding: '0.5rem 1.25rem',
                        fontSize: '0.875rem',
                        cursor: unlocking ? 'not-allowed' : 'pointer',
                        fontFamily: 'coresanscr55, sans-serif',
                      }}
                    >
                      {t('emergencyCancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleEmergencyUnlock}
                      disabled={unlocking}
                      style={{
                        background: '#DC2626',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '0.375rem',
                        padding: '0.5rem 1.25rem',
                        fontSize: '0.875rem',
                        cursor: unlocking ? 'not-allowed' : 'pointer',
                        opacity: unlocking ? 0.6 : 1,
                        fontFamily: 'coresanscr65, sans-serif',
                      }}
                    >
                      {unlocking ? '...' : t('emergencyConfirm')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        {!!patient && <Header patient={patient} />}
        <Tabs summary={summary} token={token} />
      </div>
      </LockSyncProvider>
    </ReadOnlyProvider>
  )
}

PatientContent.whyDidYouRender = true

export default memo(PatientContent)
